import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./api/AuthContext";
import { RequireRole } from "./routes/RequireRole";
import { ThemeProvider } from "./components/ThemeProvider";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CoursesPage } from "./pages/CoursesPage";
import { CourseDetailPage } from "./pages/CourseDetailPage";
import { RunsPage } from "./pages/RunsPage";
import { ApprovalsPage } from "./pages/ApprovalsPage";
import { ContentGapsPage } from "./pages/ContentGapsPage";
import { FailedExtractionsPage } from "./pages/FailedExtractionsPage";
import { TranscriptionsPage } from "./pages/TranscriptionsPage";
import { SimPatientsPage } from "./pages/SimPatientsPage";
import { SimPatientEditorPage } from "./pages/SimPatientEditorPage";
import { SimPatientGeneratePage } from "./pages/SimPatientGeneratePage";
import { SimPatientDetailPage } from "./pages/SimPatientDetailPage";
import { SimAttemptsPage } from "./pages/SimAttemptsPage";
import { SettingsPage } from "./pages/SettingsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ADMIN_ROLES = ["admin", "super_admin"];

export default function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              {/* Every admin API route requires admin/super_admin (root always
                  passes), so the whole shell is gated on those roles. */}
              <Route
                path="/*"
                element={
                  <RequireRole roles={ADMIN_ROLES}>
                    <AppShell>
                      <Routes>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/courses" element={<CoursesPage />} />
                        <Route path="/courses/:moodleId" element={<CourseDetailPage />} />
                        <Route path="/approvals" element={<ApprovalsPage />} />
                        <Route path="/runs" element={<RunsPage />} />
                        <Route path="/failed-extractions" element={<FailedExtractionsPage />} />
                        <Route path="/transcriptions" element={<TranscriptionsPage />} />
                        <Route path="/content-gaps" element={<ContentGapsPage />} />
                        <Route path="/sim-patients" element={<SimPatientsPage />} />
                        <Route path="/sim-patients/new" element={<SimPatientGeneratePage />} />
                        <Route path="/sim-patients/:id" element={<SimPatientDetailPage />} />
                        <Route path="/sim-patients/:id/edit" element={<SimPatientEditorPage />} />
                        <Route path="/sim-attempts" element={<SimAttemptsPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </AppShell>
                  </RequireRole>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
